import React from 'react';
import { FilterDropdownHeader } from 'src/components/star-grid/components/FilterDropdownHeader';
import Button from '@amzn/awsui-components-core/button';
import FormField from '@amzn/awsui-components-core/form-field';
import Multiselect from '@amzn/awsui-components-core/multiselect';
import Select from '@amzn/awsui-components-core/select';
import TokenGroup from '@amzn/awsui-components-core/token-group';
import { ReportFilterField } from 'src/components/star-grid/components/ReportFilterField';
import { hierarchyOptions, uniqueHierarchyOptions } from 'src/components/star-grid/utils/hierarchyOptions';
import type { ReportParameterFieldProps } from 'src/components/star-grid/components/ReportParameterField';
import { useReportParameter } from 'src/components/star-grid/hooks/useReportParameter';
import { useReportParameterFilter } from 'src/components/star-grid/hooks/useReportParameterFilter';
import styles from 'src/components/star-grid/components/styles/SavedReportFilters.module.scss';
import { HierarchyFilterDropdown } from 'src/components/star-grid/components/HierarchyFilterDropdown';
interface Props extends ReportParameterFieldProps {
  /** Set once the tree has been asked for; until then the tree control shows without members. */
  readonly treeWanted?: boolean;
  readonly onWantTree?: () => void;
  readonly state: ReturnType<typeof useReportParameter>;
}
export const ReportParameterFilter: React.FC<Props> = ({ parameter, values, state, disabled, error, onChange, treeWanted = true, onWantTree }) => {
  const selection = useReportParameterFilter(parameter, values, state, onChange);
  const controlId = `defined-parameter-${parameter.key}`;
  const hierarchySelection = React.useMemo(() => {
    const options = uniqueHierarchyOptions(hierarchyOptions(state.tree?.members ?? []));
    const chosen = new Set(values);
    return { options, selectedOptions: options.filter((option) => chosen.has(option.value)) };
  }, [state.tree, values]);
  // A hierarchy is a TREE, so it gets the tree control: expandable, indented, and scoped to the
  // roll-up the report was created with. It previously rendered as a flat multiselect whose only cue
  // to depth was leading space -- nothing could be collapsed, and every member of the dimension was
  // offered regardless of the report's own scope.
  // Not asked for yet, or loaded: the tree control. A tree that was asked for and never came degrades
  // to the flat list below.
  if (parameter.hierarchy_id && !state.usesDefault && (state.tree || !treeWanted))
    return (
      <FormField errorText={error ?? state.error?.message}>
        <HierarchyFilterDropdown
          id={controlId}
          label={parameter.label}
          members={state.tree?.members ?? []}
          values={values}
          multiple={parameter.multiple}
          scopeRoot={parameter.scope_root}
          disabled={disabled}
          // Nothing loads until the tree is asked for, so there is nothing to wait on before that.
          loading={treeWanted && state.loading}
          onOpen={onWantTree}
          text={selection.text}
          onChange={(next) => onChange(parameter.key, next)}
        />
      </FormField>
    );
  return (
    <FormField errorText={error ?? state.error?.message}>
      <ReportFilterField
        label={parameter.label}
        controlId={`defined-parameter-${parameter.key}`}
        value={selection.summary}
        dropdown={!state.usesDefault}
      >
        {state.usesDefault ? (
          <span className={styles.fixedValue} title={selection.title}>
            {selection.summary}
          </span>
        ) : parameter.hierarchy_id ? (
          /* A normal multiselect dropdown, depth-indented rather than the panel editor: inside a
             report the filter bar has no room for a tree grid, and an absolutely positioned panel
             was clipped by the bar's horizontal scroll. */
          <Multiselect
            data-testid={`defined-parameter-${parameter.key}`}
            controlId={`defined-parameter-${parameter.key}`}
            ariaLabel={parameter.label}
            filteringType="auto"
            expandToViewport
            hideTokens
            keepOpen
            dropdownRole="dialog"
            renderDropdownHeader={() => <FilterDropdownHeader controlId={controlId} />}
            placeholder={selection.summary}
            selectedOptions={hierarchySelection.selectedOptions}
            options={hierarchySelection.options}
            disabled={disabled}
            statusType={state.loading ? 'loading' : state.error ? 'error' : 'finished'}
            loadingText={selection.text('loadingOptions')}
            empty={selection.text('noOptions')}
            onChange={({ detail }) =>
              onChange(
                parameter.key,
                detail.selectedOptions.flatMap((item) => (item.value ? [item.value] : []))
              )
            }
          />
        ) : parameter.multiple ? (
          <Multiselect
            data-testid={`defined-parameter-${parameter.key}`}
            controlId={`defined-parameter-${parameter.key}`}
            ariaLabel={parameter.label}
            filteringType="auto"
            expandToViewport
            hideTokens
            keepOpen
            dropdownRole="dialog"
            placeholder={selection.summary}
            selectedOptions={selection.selectedOptions}
            options={selection.options}
            disabled={disabled}
            statusType={state.loading ? 'loading' : state.error ? 'error' : 'finished'}
            loadingText={selection.text('loadingOptions')}
            empty={selection.text('noOptions')}
            onChange={selection.onChange}
            renderDropdownHeader={() => (
              <FilterDropdownHeader controlId={controlId}>
                {values.length > 0 && (
                  <div className={styles.selections} data-filter-selections role="group" aria-label={selection.text('studioSelectedValues')}>
                    <TokenGroup items={selection.tokens} alignment="vertical" readOnly={disabled} onDismiss={selection.onDismiss} />
                  </div>
                )}
              </FilterDropdownHeader>
            )}
          />
        ) : (
          <Select
            data-testid={`defined-parameter-${parameter.key}`}
            controlId={`defined-parameter-${parameter.key}`}
            ariaLabel={parameter.label}
            filteringType="auto"
            expandToViewport
            dropdownRole="dialog"
            renderDropdownHeader={() => <FilterDropdownHeader controlId={controlId} />}
            selectedOption={state.selected[0] ?? null}
            options={state.data ?? []}
            disabled={disabled}
            statusType={state.loading ? 'loading' : state.error ? 'error' : 'finished'}
            placeholder={selection.text('selectValue')}
            loadingText={selection.text('loadingOptions')}
            empty={selection.text('noOptions')}
            onChange={({ detail }) => onChange(parameter.key, detail.selectedOption.value ? [detail.selectedOption.value] : [])}
          />
        )}
      </ReportFilterField>
      {state.error && (
        <Button data-testid={`defined-options-retry-${parameter.key}`} formAction="none" onClick={state.reload}>
          {selection.text('retry')}
        </Button>
      )}
    </FormField>
  );
};
